import React, { useEffect, useState } from "react";
import apiClient from "../../services/apiClient";
import Layout from "../../components/Layout";
import DynamicTable from "../../components/Table/DataTable";
import TablePaginationActions from "../../components/Table/tablePagination";
import { Paid } from "@mui/icons-material";
import { Eye, IndianRupee } from "lucide-react";
import { useNavigate } from "react-router-dom";

const SettlementPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [providers, setProviders] = useState<any[]>([]);
  const [serviceProviders, setServiceProviders] = useState<any[]>([]);
  const [specialities, setSpecialities] = useState<any[]>([]);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<number | "">("");
  const [selectedServiceProvider, setSelectedServiceProvider] = useState<number | "">("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [selectedSpeciality, setSelectedSpeciality] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const updateSettlementStatus = async (row: any, status: "paid" | "hold") => {
    if (!row?.id) return;

    const statusLabels: Record<string, string> = {
      paid: "mark as paid",
      hold: "put on hold",
    };

    const confirm = window.confirm(`Are you sure you want to ${statusLabels[status]} settlement #${row.id}?`);
    if (!confirm) return;

    try {
      setUpdatingId(row.id);

      await apiClient(`v1/resource/settlement/${row.id}`, {
        method: "PATCH",
        body: { status },
      });

      setData((prev) =>
        prev.map((d) => (d.id === row.id ? { ...d, status } : d))
      );

      alert(`✅ Settlement #${row.id} successfully ${status === "paid" ? "marked as paid" : "put on hold"}`);
    } catch (err: any) {
      alert(err?.message || "❌ Failed to update status");
    } finally {
      setUpdatingId(null);
    }
  };

  const fetchSettlements = async (
    pageNumber = 1,
    limit = 10,
    providerId?: number,
    serviceProviderId?: number,
    status?: string,
    speciality?: string,
  ) => {
    try {
      setLoading(true);
      setError(null);

      let url = `v1/resource/settlement?limit=${limit}&page=${pageNumber}`;
      if (providerId) url += `&provider_id=${providerId}`;
      if (serviceProviderId) url += `&service_provider_id=${serviceProviderId}`;
      if (status) url += `&status=${status}`;
      if (speciality) url += `&speciality=${speciality}`;

      const response: any = await apiClient(url);

      setData(response?.data?.settlements || []);
      setTotalCount(response?.data?.pagination?.total || 0);
    } catch (err: any) {
      console.error("❌ Failed to fetch settlements:", err);
      setError(err?.message || "Failed to load settlements");
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const provResp: any = await apiClient("v1/resource/providers");
      const spResp: any = await apiClient("v1/resource/service_provider/all");

      setProviders(provResp?.data?.providers || []);
      setServiceProviders(spResp?.data?.service_providers || []);

      // Collect all unique specialities
      const allSpecialities = provResp?.data?.providers
        ?.flatMap((p: any) => p.provider_speciality?.map((s: any) => s.slug) || [])
        ?.filter((v: string, i: number, a: string[]) => a.indexOf(v) === i);
      setSpecialities(allSpecialities || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchFilters();
    fetchSettlements(page + 1, rowsPerPage);
  }, []);

  useEffect(() => {
    fetchSettlements(
      page + 1,
      rowsPerPage,
      typeof selectedProvider === "number" ? selectedProvider : undefined,
      typeof selectedServiceProvider === "number" ? selectedServiceProvider : undefined,
      selectedStatus || undefined,
      selectedSpeciality || undefined
    );
  }, [page, rowsPerPage, selectedProvider, selectedServiceProvider, selectedStatus, selectedSpeciality, dateFrom, dateTo]);

  const columns = [
    { key: "serial", label: "S.No", align: "center", render: (_row: any, index: number) => page * rowsPerPage + index + 1 },
    { key: "provider_name", label: "Provider", align: "center", render: (row: any) => row.provider?.user ? `${row.provider.user.first_name} ${row.provider.user.last_name}` : "-" },
    { key: "service_provider_name", label: "Service Provider", align: "center", render: (row: any) => row.serviceProvider?.user ? `${row.serviceProvider.user.first_name} ${row.serviceProvider.user.last_name}` : "-" },
    { key: "total_amount", label: "Total", render: (row: any) => `₹${row.total_amount}`, align: "center" },
    { key: "commission", label: "Commission", render: (row: any) => `₹${row.total_commission}`, align: "center" },
    { key: "payable_amount", label: "Payable", render: (row: any) => `₹${row.payable_amount}`, align: "center" },
    {
      key: "status",
      label: "Status",
      render: (row: any) => {
        const status = row.status?.toLowerCase() || "";
        const colors: Record<string, string> = {
          paid: "#4caf50",
          pending: "#ff9800",
          failed: "#f44336",
          hold: "#ffb300", // added color for hold
        };
        return (
          <span style={{
            backgroundColor: colors[status] || "#9e9e9e",
            color: "#fff",
            padding: "4px 10px",
            borderRadius: "12px",
            fontWeight: 600,
            textTransform: "uppercase",
            fontSize: "0.875rem",
          }}>
            {row.status?.toUpperCase()}
          </span>
        );
      },
      align: "center",
    },
    {
      key: "date",
      label: "Date",
      render: (row: any) => {
        const date = new Date(row.settlement_date);
        return `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
      },
      align: "center",
    },
  ];

  const actions = [
    {
      label: "Mark Paid",
      icon: <IndianRupee />,
      color: "#4caf50",
      onClick: (row: any) => updateSettlementStatus(row, "paid"),
    },
    {
      label: "Hold",
      icon: <IndianRupee />,
      color: "#ff9800",
      onClick: (row: any) => updateSettlementStatus(row, "hold"),
    },
    {
      label: "View",
      icon: <Eye />,
      color: "#1976d2",
      onClick: (row: any) => navigate(`/settlements/${row.id}`),
    },
  ];



  return (
    <Layout>
      <div className="max-w-7xl mx-auto mt-8 px-4">
        {/* Filters */}
        <div className="relative mb-6 p-6 bg-gradient-to-br from-white via-gray-50 to-white rounded-2xl shadow-lg border border-gray-100">
          {/* Decorative Elements */}
          <div className="absolute top-0 left-0 w-32 h-32 bg-blue-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-32 h-32 bg-purple-500/5 rounded-full blur-3xl"></div>

          <div className="relative flex flex-wrap gap-4">
            {/* Provider Filter */}
            <div className="flex flex-col min-w-[200px] flex-1">
              <label className="mb-2 text-sm font-bold text-gray-800 flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                Provider
              </label>
              <div className="relative group">
                <select
                  value={selectedProvider}
                  onChange={(e) => setSelectedProvider(e.target.value ? Number(e.target.value) : "")}
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 bg-white focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 hover:border-blue-300 transition-all duration-200 appearance-none cursor-pointer shadow-sm hover:shadow-md"
                >
                  <option value="">All Providers</option>
                  {providers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.user.first_name} {p.user.last_name}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 group-hover:text-blue-500 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Service Provider Filter */}
            <div className="flex flex-col min-w-[200px] flex-1">
              <label className="mb-2 text-sm font-bold text-gray-800 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                Service Provider
              </label>
              <div className="relative group">
                <select
                  value={selectedServiceProvider}
                  onChange={(e) => setSelectedServiceProvider(e.target.value ? Number(e.target.value) : "")}
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 bg-white focus:outline-none focus:border-green-500 focus:ring-4 focus:ring-green-100 hover:border-green-300 transition-all duration-200 appearance-none cursor-pointer shadow-sm hover:shadow-md"
                >
                  <option value="">All Service Providers</option>
                  {serviceProviders.map((sp) => (
                    <option key={sp.id} value={sp.id}>
                      {sp.user.first_name} {sp.user.last_name}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 group-hover:text-green-500 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex flex-col min-w-[200px] flex-1">
              <label className="mb-2 text-sm font-bold text-gray-800 flex items-center gap-2">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                Status
              </label>
              <div className="relative group">
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 bg-white focus:outline-none focus:border-purple-500 focus:ring-4 focus:ring-purple-100 hover:border-purple-300 transition-all duration-200 appearance-none cursor-pointer shadow-sm hover:shadow-md"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">⏳ Pending</option>
                  <option value="paid">✓ Paid</option>
                  <option value="failed">✗ Failed</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 group-hover:text-purple-500 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Speciality Filter */}
            {/* <div className="flex flex-col min-w-[200px] flex-1">
              <label className="mb-2 text-sm font-bold text-gray-800 flex items-center gap-2">
                <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                Speciality
              </label>
              <div className="relative group">
                <select
                  value={selectedSpeciality}
                  onChange={(e) => setSelectedSpeciality(e.target.value)}
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 bg-white focus:outline-none focus:border-yellow-500 focus:ring-4 focus:ring-yellow-100 hover:border-yellow-300 transition-all duration-200 appearance-none cursor-pointer shadow-sm hover:shadow-md"
                >
                  <option value="">All Specialities</option>
                  {specialities.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 group-hover:text-yellow-500 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div> */}
          </div>
        </div>

        <DynamicTable
          columns={columns}
          data={data}
          loading={loading}
          error={error}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={(newPage) => setPage(newPage)}
          onRowsPerPageChange={(newRows) => { setRowsPerPage(newRows); setPage(0); }}
          actions={actions}
          ActionsComponent={TablePaginationActions}
        />
      </div>
    </Layout>
  );
};

export default SettlementPage;
